// components/menu/EditMenuItemModal.jsx
import React, { useEffect, useMemo, useState } from "react";
import "./EditMenuItemModal.css";
import {
  updateMenuItem,
  uploadMenuImage,
  createMenuItem,
  deleteMenuItem,
  fetchSideMenuItems,
  fetchAllergens,
} from "../../services/menuApi";
import { parseMenuItemToEditModel } from "./menuItemModel";
import { useAuth } from "../../auth/AuthContext";
import OnlineImageSearch from "./OnlineImageSearch";

const input = {
  padding: "10px 12px",
  borderRadius: 8,
  border: "1px solid #ddd",
  background: "#fff",
  fontSize: 14,
};
const area = { ...input, resize: "vertical" };
const btn = {
  padding: "8px 12px",
  borderRadius: 8,
  border: "1px solid #dfe3e6",
  background: "#fff",
  cursor: "pointer",
};
const btnPrimary = {
  ...btn,
  border: "1px solid rgba(255, 0, 0, 1)",
  background: "rgba(255, 0, 0, 1)",
  color: "#fff",
};
const smallBtn = { ...btn, padding: "6px 8px", fontSize: 12 };

/* ---------- Delimited option helpers: "--" between options, "<>" between name/price ---------- */
function parseOptionString(str) {
  const raw = String(str ?? "");
  if (!raw.trim()) return [];
  return raw
    .split("--")
    .map((s) => s.trim())
    .filter(Boolean)
    .map((seg) => {
      const [nameRaw = "", priceRaw = "0"] = seg.split("<>");
      const name = nameRaw.trim();
      const price = Number(priceRaw);
      return name ? { name, price: Number.isFinite(price) ? price : 0 } : null;
    })
    .filter(Boolean);
}
function serializeOptionString(options, selectedIndex = 0) {
  if (!options?.length) return "";
  const arr = options.slice();
  if (selectedIndex > 0 && selectedIndex < arr.length) {
    const [sel] = arr.splice(selectedIndex, 1);
    arr.unshift(sel);
  }
  const joined = arr
    .map((o) => `${o.name}<>${(Number(o.price) || 0).toFixed(2)}`)
    .join("--");
  return joined ? `${joined}--` : "";
}

/** Persist attached sides as id<>price (reuse) or Name<>price<>1 (new universal). */
function serializeSidesString(options, selectedIndex = 0) {
  if (!options?.length) return "";
  const arr = options.slice();
  if (selectedIndex > 0 && selectedIndex < arr.length) {
    const [sel] = arr.splice(selectedIndex, 1);
    arr.unshift(sel);
  }
  const joined = arr
    .map((o) => {
      const price = (Number(o.price) || 0).toFixed(2);
      if (o.id) return `${o.id}<>${price}`;
      const uni = o.makeUniversal || o.universal ? "<>1" : "";
      return `${o.name}<>${price}${uni}`;
    })
    .join("--");
  return joined ? `${joined}--` : "";
}

function sidesFromResolved(resolved) {
  if (!Array.isArray(resolved) || !resolved.length) return null;
  return resolved
    .filter((s) => s && (s.name || s.item))
    .map((s) => ({
      id: s.id ? String(s.id) : undefined,
      name: s.name || s.item || "",
      price: Number(s.price) || 0,
      universal:
        Number(s.universal) === 1 ||
        s.universal === true ||
        String(s.universal) === "1",
    }));
}

function matchSidesToCatalog(options, catalog) {
  if (!catalog?.length) return options || [];
  return (options || []).map((o) => {
    if (o.id) {
      const byId = catalog.find((c) => String(c.id) === String(o.id));
      if (byId) {
        return {
          ...o,
          id: String(byId.id),
          name: byId.item || o.name,
          universal: true,
        };
      }
    }
    const byName = catalog.find(
      (c) =>
        String(c.item || "").toLowerCase() === String(o.name || "").toLowerCase()
    );
    if (byName) {
      return {
        ...o,
        id: String(byName.id),
        name: byName.item || o.name,
        universal: true,
      };
    }
    return o;
  });
}
function formatPriceDisplay(n) {
  const v = Number(n || 0);
  return `P${v.toFixed(2)}`;
}
function filenameFromValue(v) {
  const s = String(v || "").trim();
  if (!s) return "";
  try {
    const url = new URL(s, window.location.origin);
    const path = url.pathname || s;
    return path.split("/").filter(Boolean).pop() || s;
  } catch {
    return s.split("/").filter(Boolean).pop() || s;
  }
}

/* ---------- Small modal wrapper (child popup) ---------- */
function SmallModal({ title, onClose, children, width = 680 }) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      className="edit-menu-item-small-modal-overlay"
      onClick={onClose}
    >
      <div
        className="edit-menu-item-small-modal-content"
        onClick={(e) => e.stopPropagation()}
        style={{ width }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 8,
            flexShrink: 0,
          }}
        >
          <div style={{ fontWeight: 800 }}>{title}</div>
          <button type="button" style={smallBtn} onClick={onClose}>
            Close
          </button>
        </div>
        <div className="edit-menu-item-small-modal-body">{children}</div>
      </div>
    </div>
  );
}

/* ---------- Reusable Option Editor ---------- */
function OptionEditor({ options, defaultIndex, onSave }) {
  const [rows, setRows] = useState(() => options?.map((o) => ({ ...o })) ?? []);
  const [defIdx, setDefIdx] = useState(defaultIndex ?? 0);

  const addRow = () => {
    setRows((r) => [...r, { name: "", price: 0 }]);
    if (rows.length === 0) setDefIdx(0);
  };
  const delRow = (i) => {
    setRows((r) => {
      const copy = r.slice();
      copy.splice(i, 1);
      let nd = defIdx;
      if (i === defIdx) nd = 0;
      else if (i < defIdx) nd = Math.max(0, defIdx - 1);
      setDefIdx(Math.min(nd, Math.max(0, copy.length - 1)));
      return copy;
    });
  };
  const move = (i, dir) => {
    setRows((r) => {
      const j = i + dir;
      if (j < 0 || j >= r.length) return r;
      const copy = r.slice();
      [copy[i], copy[j]] = [copy[j], copy[i]];
      if (defIdx === i) setDefIdx(j);
      else if (defIdx === j) setDefIdx(i);
      return copy;
    });
  };
  const setName = (i, v) =>
    setRows((r) => {
      const c = r.slice();
      c[i] = { ...c[i], name: v };
      return c;
    });
  const setPrice = (i, v) => {
    const cleaned = String(v ?? "").replace(/[^\d.]/g, "");
    const num = Number(cleaned);
    setRows((r) => {
      const c = r.slice();
      c[i] = { ...c[i], price: Number.isFinite(num) ? num : 0 };
      return c;
    });
  };
  const makeDefault = (i) => setDefIdx(i);

  const canSave = rows.length > 0 && rows.every((r) => r.name.trim().length > 0);

  return (
    <>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 140px 220px",
          gap: 8,
          alignItems: "center",
          fontSize: 13,
          color: "#666",
          marginBottom: 6,
        }}
      >
        <div>Name</div>
        <div>Price</div>
        <div>Actions</div>
      </div>

      {rows.map((r, i) => (
        <div
          key={`row-${i}`}
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 140px 220px",
            gap: 8,
            alignItems: "center",
            marginBottom: 6,
          }}
        >
          <input
            style={input}
            value={r.name}
            onChange={(e) => setName(i, e.target.value)}
            placeholder="e.g., Small / Standard / With Fries"
          />
          <input
            style={input}
            value={r.price}
            onChange={(e) => setPrice(i, e.target.value)}
            placeholder="0.00"
          />
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            <button
              type="button"
              style={smallBtn}
              onClick={() => move(i, -1)}
              disabled={i === 0}
            >
              ↑
            </button>
            <button
              type="button"
              style={smallBtn}
              onClick={() => move(i, +1)}
              disabled={i === rows.length - 1}
            >
              ↓
            </button>
            <button
              type="button"
              style={smallBtn}
              onClick={() => makeDefault(i)}
              disabled={defIdx === i}
            >
              Make default
            </button>
            <button
              type="button"
              style={{ ...smallBtn, borderColor: "#c33", color: "#c33" }}
              onClick={() => delRow(i)}
            >
              Delete
            </button>
          </div>
        </div>
      ))}

      {rows.length === 0 && (
        <div style={{ color: "#888", fontSize: 13, margin: "8px 0" }}>
          No options yet.
        </div>
      )}

      <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
        <button type="button" style={smallBtn} onClick={addRow}>
          Add option
        </button>
        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          <button
            type="button"
            style={btn}
            onClick={() => onSave(null, null, { cancel: true })}
          >
            Cancel
          </button>
          <button
            type="button"
            style={btnPrimary}
            disabled={!canSave}
            onClick={() => {
              const cleaned = rows
                .filter((r) => r.name.trim().length > 0)
                .map((r) => ({ ...r, price: Number(r.price) || 0 }));
              const nd = Math.min(defIdx, Math.max(0, cleaned.length - 1));
              onSave(cleaned, nd);
            }}
          >
            Save list
          </button>
        </div>
      </div>
    </>
  );
}

function allergenTitleKey(title) {
  return String(title || "")
    .trim()
    .toLowerCase();
}

function usableAllergenId(value) {
  const id = String(value ?? "").trim();
  if (!id || id === "0" || id === "undefined" || id === "null") return "";
  return id;
}

function catalogRowKey(row, index) {
  const title = allergenTitleKey(row?.title);
  if (title) return `t:${title}`;
  const id = usableAllergenId(row?.id ?? row?.allergen_id);
  if (id) return `id:${id}`;
  return `i:${index}`;
}

function allergenIdsFromItem(item) {
  const keys = [];
  const seen = new Set();
  const add = (key) => {
    if (!key || seen.has(key)) return;
    seen.add(key);
    keys.push(key);
  };

  const resolved = item?.allergens_resolved || item?.raw?.allergens_resolved;
  if (Array.isArray(resolved)) {
    for (const a of resolved) {
      const title = allergenTitleKey(a?.title || a?.name);
      if (title) add(`t:${title}`);
      const id = usableAllergenId(a?.id ?? a?.allergen_id);
      if (id) add(`id:${id}`);
    }
  }

  const titleBlob =
    item?.allergens?.titles ||
    item?.raw?.allergens_titles ||
    item?.allergens_titles ||
    "";
  const titleList = Array.isArray(titleBlob)
    ? titleBlob
    : String(titleBlob).split(/--|,/);
  for (const t of titleList) {
    const title = allergenTitleKey(t);
    if (title && title !== "none") add(`t:${title}`);
  }

  const ids = item?.raw?.allergens_ids || item?.allergens_ids || "";
  for (const s of String(ids).split(/--|,/)) {
    const id = usableAllergenId(s);
    if (id) add(`id:${id}`);
  }
  return keys;
}

function rowIsSelected(row, index, selectedSet) {
  if (selectedSet.has(catalogRowKey(row, index))) return true;
  const id = usableAllergenId(row?.id ?? row?.allergen_id);
  if (id && (selectedSet.has(id) || selectedSet.has(`id:${id}`))) return true;
  const title = allergenTitleKey(row?.title);
  if (title && (selectedSet.has(title) || selectedSet.has(`t:${title}`))) return true;
  return false;
}

function normalizeAllergenSelection(selectedKeys, catalog) {
  const selected = new Set((selectedKeys || []).map(String));
  return (catalog || [])
    .map((row, index) =>
      rowIsSelected(row, index, selected) ? catalogRowKey(row, index) : null
    )
    .filter(Boolean);
}

function allergenPayloadFromSelection(selectedKeys, catalog) {
  const selected = new Set((selectedKeys || []).map(String));
  const ids = [];
  const titles = [];
  (catalog || []).forEach((row, index) => {
    if (!rowIsSelected(row, index, selected)) return;
    const id = usableAllergenId(row?.id ?? row?.allergen_id);
    if (id && /^\d+$/.test(id)) ids.push(id);
    if (row?.title) titles.push(String(row.title).trim());
  });
  return {
    ids: [...new Set(ids)],
    titles: [...new Set(titles.filter(Boolean))],
  };
}

function AllergenPicker({ catalog, selectedIds, onToggle }) {
  if (!catalog?.length) {
    return (
      <div style={{ fontSize: 12, color: "#888" }}>
        No allergens in the library yet. Create them from Menu → Allergens.
      </div>
    );
  }
  const selected = new Set((selectedIds || []).map(String));
  return (
    <div className="allergen-picker-grid" role="group" aria-label="Allergens">
      {catalog.map((row, index) => {
        const key = catalogRowKey(row, index);
        const checked = rowIsSelected(row, index, selected);
        return (
          <button
            type="button"
            key={key}
            className={`allergen-picker-chip${checked ? " is-on" : ""}`}
            title={row.description || row.title}
            aria-pressed={checked}
            onClick={() => onToggle(key)}
          >
            <span
              className={`allergen-picker-box${checked ? " is-on" : ""}`}
              aria-hidden="true"
            />
            <span>{row.title}</span>
          </button>
        );
      })}
    </div>
  );
}

/* ---------- Sides editor: attach Universal sides or add item-specific extras ---------- */
function SidesEditor({ options, defaultIndex, universalSides, onSave }) {
  const [rows, setRows] = useState(() => options?.map((o) => ({ ...o })) ?? []);
  const [defIdx, setDefIdx] = useState(defaultIndex ?? 0);
  const [pickId, setPickId] = useState("");

  const attachedIds = new Set(rows.map((r) => String(r.id || "")).filter(Boolean));
  const available = (universalSides || []).filter(
    (s) => !attachedIds.has(String(s.id))
  );

  const addFromLibrary = () => {
    const side = (universalSides || []).find((s) => String(s.id) === String(pickId));
    if (!side) return;
    setRows((r) => {
      if (r.some((x) => String(x.id) === String(side.id))) return r;
      return [
        ...r,
        {
          id: String(side.id),
          name: side.item,
          price: Number(side.price) || 0,
          universal: true,
        },
      ];
    });
    setPickId("");
    setDefIdx((d) => (rows.length === 0 ? 0 : d));
  };

  const addCustom = () => {
    setRows((r) => [...r, { name: "", price: 0, makeUniversal: false }]);
    if (rows.length === 0) setDefIdx(0);
  };

  const delRow = (i) => {
    setRows((r) => {
      const copy = r.slice();
      copy.splice(i, 1);
      let nd = defIdx;
      if (i === defIdx) nd = 0;
      else if (i < defIdx) nd = Math.max(0, defIdx - 1);
      setDefIdx(Math.min(nd, Math.max(0, copy.length - 1)));
      return copy;
    });
  };
  const move = (i, dir) => {
    setRows((r) => {
      const j = i + dir;
      if (j < 0 || j >= r.length) return r;
      const copy = r.slice();
      [copy[i], copy[j]] = [copy[j], copy[i]];
      if (defIdx === i) setDefIdx(j);
      else if (defIdx === j) setDefIdx(i);
      return copy;
    });
  };
  const setName = (i, v) =>
    setRows((r) => {
      const c = r.slice();
      c[i] = { ...c[i], name: v };
      return c;
    });
  const setPrice = (i, v) => {
    const cleaned = String(v ?? "").replace(/[^\d.]/g, "");
    const num = Number(cleaned);
    setRows((r) => {
      const c = r.slice();
      c[i] = { ...c[i], price: Number.isFinite(num) ? num : 0 };
      return c;
    });
  };
  const setMakeUniversal = (i, checked) =>
    setRows((r) => {
      const c = r.slice();
      c[i] = { ...c[i], makeUniversal: checked };
      return c;
    });

  const canSave = rows.every((r) => String(r.name || "").trim().length > 0);

  return (
    <>
      <div
        style={{
          display: "flex",
          gap: 8,
          alignItems: "center",
          marginBottom: 12,
          flexWrap: "wrap",
        }}
      >
        <select
          style={{ ...input, flex: 1, minWidth: 180 }}
          value={pickId}
          onChange={(e) => setPickId(e.target.value)}
        >
          <option value="">Add from Universal Sides…</option>
          {available.map((s) => (
            <option key={s.id} value={String(s.id)}>
              {s.item} — {formatPriceDisplay(s.price)}
            </option>
          ))}
        </select>
        <button
          type="button"
          style={smallBtn}
          onClick={addFromLibrary}
          disabled={!pickId}
        >
          Attach
        </button>
      </div>
      {available.length === 0 && (universalSides || []).length > 0 && (
        <div style={{ fontSize: 12, color: "#888", marginBottom: 8 }}>
          All universal sides are already attached to this item.
        </div>
      )}
      {(universalSides || []).length === 0 && (
        <div style={{ fontSize: 12, color: "#888", marginBottom: 8 }}>
          No universal sides in the library yet. Create them from Menu → Universal Sides,
          or add a custom side below and check “Save as universal”.
        </div>
      )}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 110px 90px 220px",
          gap: 8,
          alignItems: "center",
          fontSize: 13,
          color: "#666",
          marginBottom: 6,
        }}
      >
        <div>Name</div>
        <div>Price</div>
        <div>Shared</div>
        <div>Actions</div>
      </div>

      {rows.map((r, i) => (
        <div
          key={`side-row-${r.id || i}`}
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 110px 90px 220px",
            gap: 8,
            alignItems: "center",
            marginBottom: 6,
          }}
        >
          <input
            style={{
              ...input,
              background: r.universal || r.id ? "#f7f7f7" : "#fff",
            }}
            value={r.name}
            onChange={(e) => setName(i, e.target.value)}
            placeholder="e.g., Onion Rings"
            readOnly={!!(r.universal || r.id)}
          />
          <input
            style={input}
            value={r.price}
            onChange={(e) => setPrice(i, e.target.value)}
            placeholder="0.00"
          />
          <div style={{ fontSize: 12, color: "#555" }}>
            {r.universal || r.id ? (
              <span
                style={{
                  display: "inline-block",
                  padding: "2px 8px",
                  borderRadius: 999,
                  background: "#fde8e8",
                  color: "#b3261e",
                  fontWeight: 700,
                }}
              >
                Universal
              </span>
            ) : (
              <label style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <input
                  type="checkbox"
                  checked={!!r.makeUniversal}
                  onChange={(e) => setMakeUniversal(i, e.target.checked)}
                />
                Save
              </label>
            )}
          </div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            <button type="button" style={smallBtn} onClick={() => move(i, -1)} disabled={i === 0}>
              ↑
            </button>
            <button
              type="button"
              style={smallBtn}
              onClick={() => move(i, +1)}
              disabled={i === rows.length - 1}
            >
              ↓
            </button>
            <button
              type="button"
              style={smallBtn}
              onClick={() => setDefIdx(i)}
              disabled={defIdx === i}
            >
              Default
            </button>
            <button
              type="button"
              style={{ ...smallBtn, borderColor: "#c33", color: "#c33" }}
              onClick={() => delRow(i)}
            >
              Remove
            </button>
          </div>
        </div>
      ))}

      {rows.length === 0 && (
        <div style={{ color: "#888", fontSize: 13, margin: "8px 0" }}>
          No sides on this item yet.
        </div>
      )}

      <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
        <button type="button" style={smallBtn} onClick={addCustom}>
          Add custom side
        </button>
        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          <button
            type="button"
            style={btn}
            onClick={() => onSave(null, null, { cancel: true })}
          >
            Cancel
          </button>
          <button
            type="button"
            style={btnPrimary}
            disabled={!canSave}
            onClick={() => {
              const cleaned = rows
                .filter((row) => String(row.name || "").trim().length > 0)
                .map((row) => ({ ...row, price: Number(row.price) || 0 }));
              const nd = Math.min(defIdx, Math.max(0, cleaned.length - 1));
              onSave(cleaned, nd);
            }}
          >
            Save list
          </button>
        </div>
      </div>
    </>
  );
}

/* ---------- Star control ---------- */
function Stars({ value = 0, onChange }) {
  const [hover, setHover] = useState(null);
  return (
    <div style={{ display: "flex", gap: 4 }}>
      {[1, 2, 3, 4, 5].map((i) => (
        <span
          key={i}
          onMouseEnter={() => setHover(i)}
          onMouseLeave={() => setHover(null)}
          onClick={() => onChange?.(i)}
          style={{
            fontSize: 20,
            cursor: "pointer",
            color: (hover ?? value) >= i ? "#ffbf00" : "#ccc",
          }}
          aria-label={`${i} star${i > 1 ? "s" : ""}`}
        >
          ★
        </span>
      ))}
    </div>
  );
}

function basenameFromPathish(s) {
  // handles http(s)://..., /admin/images/foo.png, C:\fakepath\foo.png, etc.
  if (!s) return "";
  try {
    // If it's a URL, use URL API
    const u = new URL(String(s), window.location.origin);
    const parts = (u.pathname || "").split("/").filter(Boolean);
    if (parts.length) return parts[parts.length - 1];
  } catch {
    // not a valid URL, fall through
  }
  const cleaned = String(s).replace(/^[A-Za-z]:\\fakepath\\/, ""); // Windows fakepath
  const parts = cleaned.split(/[\\/]/).filter(Boolean);
  return parts.length ? parts[parts.length - 1] : cleaned;
}

const MENU_IMAGE_REDIR = "https://mydata.tablemate.net/redir.php?url=";

function tenantImageHost(tenantServerUrl) {
  return String(tenantServerUrl || "")
    .replace(/\/+$/, "")
    .replace(/\/tablemate$/i, "");
}

function extractMenuImageFilename(value) {
  const s = String(value || "").trim();
  if (!s) return "";
  const noQuery = s.split("?")[0];
  const stripped = noQuery.replace(/^\/?(tablemate\/)?admin\/images\//i, "");
  return filenameFromValue(stripped);
}

/**
 * Preview URL for a menu item image.
 * Bare filenames and /admin/images/ paths are proxied through redir.php so
 * the BESA app can load tenant files. Remote https URLs (search results)
 * are used as-is so the photo you just picked shows immediately.
 */
function buildMenuImagePreviewUrl(tenantServerUrl, value, cacheBust) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (/^(data:|blob:)/i.test(raw)) return raw;

  // Existing items already store a redir.php preview — keep it, and
  // inject /tablemate once if the encoded path is still /admin/images/.
  if (/redir\.php/i.test(raw)) {
    if (raw.includes("%2Fadmin%2F") && !raw.includes("%2Ftablemate%2Fadmin%2F")) {
      return raw.replace("%2Fadmin%2F", "%2Ftablemate%2Fadmin%2F");
    }
    return raw;
  }

  const isTenantAdminPath = /\/admin\/images\//i.test(raw);
  if (/^https?:\/\//i.test(raw) && !isTenantAdminPath) {
    return raw;
  }

  const filename = extractMenuImageFilename(raw);
  const host = tenantImageHost(tenantServerUrl);
  if (filename && host) {
    const upstream = `${host}/tablemate/admin/images/${filename}${
      cacheBust ? `?t=${cacheBust}` : ""
    }`;
    return `${MENU_IMAGE_REDIR}${encodeURIComponent(upstream)}`;
  }

  return /^https?:\/\//i.test(raw) ? raw : "";
}

function sanitizeFilenameJS(name) {
  const base = basenameFromPathish(name);
  let clean = base
    .replace(/[^A-Za-z0-9._-]+/g, "_")
    .replace(/^[_\.-]+|[_\.-]+$/g, "");
  return clean || "image";
}

function buildFallbackModel(item) {
  return {
    id: item?.id ?? "",
    name: item?.item ?? item?.name ?? "",
    price1: item?.price1 ?? "",
    price2: item?.price2 ?? "",
    rating: item?.rating ?? 3,
    quantity: item?.quantity ?? "",
    warning: item?.warning ?? "",
    visible: item?.visibility === "1" || item?.visible,
    description: item?.description ?? "",
    url: item?.url ?? "",
    subcode: item?.subcode ?? "",
    sides: item?.sides ?? "",
    sizes: item?.sizes ?? "",
    specifications: item?.specifications ?? "",
    imagePreview: "",
  };
}

export default function EditMenuItemModal({
  code,
  open,
  item,
  parentPath,
  onClose,
  onSaved,
}) {
  const { tenantServerUrl } = useAuth();

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  // Initial model – SAFE, no parseMenuItemToEditModel here to avoid early crash
  const [model, setModel] = useState(() => buildFallbackModel(item || {}));

  // Option lists + selected indices
  const [sidesOptions, setSidesOptions] = useState([]);
  const [sidesIndex, setSidesIndex] = useState(0);
  const [sizesOptions, setSizesOptions] = useState([]);
  const [sizesIndex, setSizesIndex] = useState(0);
  const [specOptions, setSpecOptions] = useState([]);
  const [specIndex, setSpecIndex] = useState(0);
  const [universalSides, setUniversalSides] = useState([]);
  const [allergenCatalog, setAllergenCatalog] = useState([]);
  const [selectedAllergenIds, setSelectedAllergenIds] = useState([]);

  // Child modal visibility
  const [editSidesOpen, setEditSidesOpen] = useState(false);
  const [editSizesOpen, setEditSizesOpen] = useState(false);
  const [editSpecsOpen, setEditSpecsOpen] = useState(false);
  const [photoOpen, setPhotoOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [previewOverride, setPreviewOverride] = useState("");

  // Upload state
  const [uploading, setUploading] = useState(false);
  const [uploadErr, setUploadErr] = useState("");
  const [fileSelected, setFileSelected] = useState(null);
  const [pendingUpload, setPendingUpload] = useState(false);

  const isNew = !item?.id;
  const title = isNew ? "Add Menu Item" : "Edit Menu Item";

  useEffect(() => {
    if (!open) return;

    setErr("");
    setBusy(false);
    setPreviewOverride("");

    let parsed;
    try {
      // Inject tenantServerUrl into DTO so menuItemModel’s multi-tenant logic is happy
      const dto = {
        ...(item || {}),
        server_url: tenantServerUrl,
        server: tenantServerUrl,
      };
      parsed = parseMenuItemToEditModel(dto);
    } catch (e) {
      console.error(
        "[EditMenuItemModal] parseMenuItemToEditModel failed, using fallback model:",
        e
      );
      parsed = buildFallbackModel(item || {});
    }

    setModel({
      ...parsed,
      warning: parsed.warning ?? item?.warning ?? "",
    });

    const _sizes = parseOptionString(parsed.sizes);
    const _specs = parseOptionString(parsed.specifications);

    setSidesIndex(0);
    setSizesOptions(_sizes);
    setSizesIndex(0);
    setSpecOptions(_specs);
    setSpecIndex(0);

    setEditSidesOpen(false);
    setEditSizesOpen(false);
    setEditSpecsOpen(false);
    setPhotoOpen(false);
    setSearchOpen(false);

    setUploading(false);
    setUploadErr("");
    setFileSelected(null);
    setPendingUpload(false);

    setSelectedAllergenIds(allergenIdsFromItem(item));

    let cancelled = false;
    (async () => {
      let catalog = [];
      let allergens = [];
      try {
        if (tenantServerUrl) {
          [catalog, allergens] = await Promise.all([
            fetchSideMenuItems(tenantServerUrl, { universal: true }),
            fetchAllergens(tenantServerUrl).catch((ex) => {
              console.warn("[EditMenuItemModal] failed to load allergens", ex);
              return [];
            }),
          ]);
        }
      } catch (ex) {
        console.warn("[EditMenuItemModal] failed to load universal sides", ex);
      }
      if (cancelled) return;
      setUniversalSides(catalog);
      setAllergenCatalog(allergens);
      setSelectedAllergenIds((prev) =>
        normalizeAllergenSelection(prev, allergens)
      );
      const resolved = sidesFromResolved(
        item?.sides_resolved || item?.raw?.sides_resolved
      );
      const parsedSides = resolved || parseOptionString(parsed.sides);
      setSidesOptions(matchSidesToCatalog(parsedSides, catalog));
    })();

    return () => {
      cancelled = true;
    };
  }, [open, item?.id, tenantServerUrl]);

  const setField = (k, v) => setModel((m) => ({ ...m, [k]: v }));
  const setDigits = (k, v, maxLen) =>
    setField(
      k,
      String(v || "")
        .replace(/\D/g, "")
        .slice(0, maxLen ?? 99)
    );

  const previewSrc = useMemo(() => {
    const src = model.imagePreview || model.url || "";
    const built = buildMenuImagePreviewUrl(tenantServerUrl, src);
    console.log("[IMAGE]", built);
    return built;
  }, [model.imagePreview, model.url, tenantServerUrl]);

  const displayPreviewSrc = previewOverride || previewSrc;

  const handlePreviewError = () => {
    if (previewOverride) return;
    const fallback = buildMenuImagePreviewUrl(
      tenantServerUrl,
      model.url,
      Date.now()
    );
    if (fallback && fallback !== previewSrc) {
      setPreviewOverride(fallback);
    }
  };

  if (!open) return null;

  // Upload handlers
  const onFilePick = (e) => {
    setUploadErr("");
    const file = e?.target?.files?.[0];
    if (!file) {
      setFileSelected(null);
      setPendingUpload(false);
      return;
    }

    if (!file.type.startsWith("image/")) {
      setUploadErr("Please select an image file.");
      setFileSelected(null);
      setPendingUpload(false);
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setUploadErr("Image is too large. Max 5 MB.");
      setFileSelected(null);
      setPendingUpload(false);
      return;
    }

    setFileSelected(file);
    setPendingUpload(true);
    setPreviewOverride("");

    const safeName = sanitizeFilenameJS(file.name);
    setModel((m) => ({ ...m, url: safeName })); // server expects filename only

    const reader = new FileReader();
    reader.onload = () => setModel((m) => ({ ...m, imagePreview: reader.result }));
    reader.readAsDataURL(file);
  };

  const doUpload = async () => {
    if (!fileSelected) {
      setUploadErr("Choose an image first.");
      return null;
    }
    setUploading(true);
    setUploadErr("");
    try {
      console.debug("[upload] starting", {
        name: fileSelected.name,
        size: fileSelected.size,
        type: fileSelected.type,
        code,
      });
      const res = await uploadMenuImage(tenantServerUrl,
        fileSelected,
        {
          code,
          tenantServerUrl: tenantServerUrl || undefined,
        },
        false
      ); // posts multipart via redir.php
      console.debug("[upload] response", res);

      const serverFilename =
        res && res.filename
          ? String(res.filename)
          : filenameFromValue(res?.url || fileSelected.name);

      const finalFilename = filenameFromValue(serverFilename);
      console.log("IMAGE FILE NAME "+ finalFilename)
      setModel((m) => ({
        ...m,
        url: finalFilename, // filename only
        imagePreview: buildMenuImagePreviewUrl(
          tenantServerUrl,
          finalFilename,
          Date.now()
        ),
      }));

      setFileSelected(null);
      setPendingUpload(false);
      return finalFilename; // return for save()
    } catch (ex) {
      console.error("[upload] failed", ex);
      setUploadErr(ex?.message || "Upload failed.");
      return null;
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!item?.id) return;
    const ok = window.confirm(`Delete item "${item.name}"?`);
    if (!ok) return;
    try {
      await deleteMenuItem(tenantServerUrl,{ id: item.id });
      onSaved?.();
    } catch (e) {
      alert(e?.message || "Delete failed");
    }
  };

  const save = async (e) => {
    e?.preventDefault?.();
    setErr("");
    setBusy(true);
    try {
      // ensure the new image is actually on the server
      if (pendingUpload && fileSelected) {
        const f = await doUpload();
        if (!f) throw new Error("Image upload failed; item not saved.");
      }

      const sidesStr = serializeSidesString(sidesOptions, sidesIndex);
      const sizesStr = serializeOptionString(sizesOptions, sizesIndex);
      const specStr = serializeOptionString(specOptions, specIndex);
      const allergenPick = allergenPayloadFromSelection(
        selectedAllergenIds,
        allergenCatalog
      );
      const allergenIdsOut = [...allergenPick.ids, ...allergenPick.titles].join(
        "--"
      );

      let payload = {};

      if (isNew) {
        payload = {
          item: String(model.name || "").trim(),
          price1: String(model.price1 || "").replace(/\D/g, "") || "0",
          price2:
            (String(model.price2 || "").replace(/\D/g, "").slice(0, 2) ||
              "0").padEnd(2, "0"),
          rating: String(Number(model.rating ?? 3)),
          quantity: String(model.quantity || "").replace(/\D/g, "") || "0",
          warning: String(model.warning || "").replace(/\D/g, "") || "0",
          visibility: model.visible ? "1" : "0",
          description: String(model.description || "").trim(),
          url: filenameFromValue(model.url), // filename only
          sizes: sizesStr,
          specifications: specStr,
          sides: sidesStr,
          allergen_ids: allergenIdsOut,
          bywho:
            (typeof window !== "undefined" &&
              (window.CURRENT_USER_EMAIL || window.loggedUser_Email)) ||
            "system",
          code: String(code ?? ""),
          subcode: model.subcode,
        };
        console.log("[NEW SAVE] "+tenantServerUrl)
        await createMenuItem(tenantServerUrl,payload);
      } else {
        payload = {
          id: String(model.id || "").trim(),
          item: String(model.name || "").trim(),
          price1: String(model.price1 || "").replace(/\D/g, "") || "0",
          price2:
            (String(model.price2 || "").replace(/\D/g, "").slice(0, 2) ||
              "0").padEnd(2, "0"),
          rating: String(Number(model.rating ?? 3)),
          quantity: String(model.quantity || "").replace(/\D/g, "") || "0",
          warning: String(model.warning || "").replace(/\D/g, "") || "0",
          visibility: model.visible ? "1" : "0",
          description: String(model.description || "").trim(),
          url: filenameFromValue(model.url), // filename only
          sizes: sizesStr,
          specifications: specStr,
          sides: sidesStr,
          allergen_ids: allergenIdsOut,
          bywho:
            (typeof window !== "undefined" &&
              (window.CURRENT_USER_EMAIL || window.loggedUser_Email)) ||
            "system",
          code: String(code ?? ""),
        };
 console.log("[SAVE] "+tenantServerUrl)
        await updateMenuItem(tenantServerUrl,payload);
      }

      console.debug("[save] payload", payload);

      await onSaved?.();
      onClose?.();
    } catch (ex) {
      console.error("[save] failed", ex);
      setErr(ex?.message || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  // Select handlers
  const onChangeSides = (e) => {
    const idx = Number(e.target.value);
    setSidesIndex(idx);
    setField("sides", serializeSidesString(sidesOptions, idx));
  };
  const onChangeSizes = (e) => {
    const idx = Number(e.target.value);
    setSizesIndex(idx);
    setField("sizes", serializeOptionString(sizesOptions, idx));
  };
  const onChangeSpecs = (e) => {
    const idx = Number(e.target.value);
    setSpecIndex(idx);
    setField("specifications", serializeOptionString(specOptions, idx));
  };

  // Apply from child editors
  const applySidesList = (list, defIdx, meta) => {
    if (meta?.cancel || !list) return;
    setSidesOptions(list);
    setSidesIndex(defIdx);
    setField("sides", serializeSidesString(list, defIdx));
  };
  const applySizesList = (list, defIdx, meta) => {
    if (meta?.cancel) return;
    setSizesOptions(list);
    setSizesIndex(defIdx);
    setField("sizes", serializeOptionString(list, defIdx));
  };
  const applySpecsList = (list, defIdx, meta) => {
    if (meta?.cancel) return;
    setSpecOptions(list);
    setSpecIndex(defIdx);
    setField("specifications", serializeOptionString(list, defIdx));
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="edit-menu-item-modal-overlay"
      onClick={onClose}
    >
      <div
        className="edit-menu-item-modal-content edit-menu-item-modal-dialog"
        onClick={(e) => e.stopPropagation()}
      >
        {/* header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 8,
          }}
        >
          <div>
            <div style={{ fontSize: 12, color: "#666", marginBottom: 3 }}>
              {parentPath || "Menu"}
            </div>
            <div style={{ fontWeight: 800, fontSize: 20 }}>{title}</div>
          </div>
          <button type="button" onClick={onClose} style={btn}>
            Close
          </button>
        </div>

        {/* IDs */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap: 10,
            marginBottom: 10,
          }}
        >
          <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <span style={{ fontSize: 12, color: "#555" }}>ID</span>
            <input
              style={{ ...input, background: "#f7f7f7" }}
              value={model.id ?? ""}
              readOnly
            />
          </label>
          <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <span style={{ fontSize: 12, color: "#555" }}>
              Main Menu Category Code
            </span>
            <input
              style={{ ...input, background: "#f7f7f7" }}
              value={code}
              readOnly
            />
          </label>
          <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <span style={{ fontSize: 12, color: "#555" }}>
              Menu Subcategory Code
            </span>
            <input
              style={{ ...input, background: "#f7f7f7" }}
              value={model.subcode ?? ""}
              readOnly
            />
          </label>
        </div>

        {/* Name + Price + Quantity + Warning */}
        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span style={{ fontSize: 12, color: "#555" }}>
            Menu Item Display Name
          </span>
          <input
            style={input}
            value={model.name}
            onChange={(e) => setField("name", e.target.value)}
            placeholder="Item name"
          />
        </label>

        <div
          style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}
        >
          <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <span style={{ fontSize: 12, color: "#555" }}>Base Price — Pula</span>
            <input
              style={input}
              value={model.price1}
              onChange={(e) => setDigits("price1", e.target.value)}
            />
          </label>
          <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <span style={{ fontSize: 12, color: "#555" }}>
              Base Price — Thebe
            </span>
            <input
              style={input}
              value={model.price2}
              onChange={(e) => setDigits("price2", e.target.value, 2)}
            />
          </label>
        </div>

        <div
          style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}
        >
          <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <span style={{ fontSize: 12, color: "#555" }}>No. in Stock</span>
            <input
              style={input}
              value={model.quantity}
              onChange={(e) => setDigits("quantity", e.target.value)}
            />
          </label>
          <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <span style={{ fontSize: 12, color: "#555" }}>
              Stock Warning Level{" "}
              <i>(Quantity in-stock to send inventory alerts)</i>
            </span>
            <input
              style={input}
              value={model.warning}
              onChange={(e) => setDigits("warning", e.target.value)}
            />
          </label>
        </div>

        {/* Description */}
        <div style={{ marginTop: 10 }}>
          <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <span style={{ fontSize: 12, color: "#555" }}>Description</span>
            <textarea
              rows={3}
              style={area}
              value={model.description}
              onChange={(e) => setField("description", e.target.value)}
            />
          </label>
        </div>

        {/* Image + Rating + Visibility */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 16,
            marginTop: 10,
          }}
        >
          <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
            <div
              style={{
                width: 160,
                height: 120,
                border: "1px solid #eee",
                borderRadius: 10,
                display: "grid",
                placeItems: "center",
                overflow: "hidden",
                background: "#fff",
              }}
            >
              {displayPreviewSrc ? (
                <img
                  src={displayPreviewSrc}
                  alt="Menu item"
                  referrerPolicy="no-referrer"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                    cursor: "pointer",
                  }}
                  onError={handlePreviewError}
                  onClick={(e) => {
                    e.stopPropagation();
                    setPhotoOpen(true);
                  }}
                />
              ) : (
                <span style={{ color: "#999", fontSize: 12 }}>No image</span>
              )}
            </div>

            <div style={{ display: "grid", gap: 8 }}>
              <input
                style={input}
                value={model.url}
                onChange={(e) => setField("url", e.target.value)}
                placeholder="Image filename (e.g., Grilled_Lobster_Tail_51.png) or absolute URL"
              />
              <div style={{ fontSize: 12, color: "#666" }}>
                Tip: If you enter just a filename, app will render from{" "}
                <code>/admin/images/&lt;file&gt;</code>.
              </div>

              <div
                style={{
                  display: "flex",
                  gap: 8,
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                <input
                  type="file"
                  accept="image/*"
                  onChange={onFilePick}
                  style={{ maxWidth: 240 }}
                />
                <button
                  type="button"
                  style={smallBtn}
                  onClick={(e) => {
                    e.stopPropagation();
                    doUpload();
                  }}
                  disabled={uploading || !fileSelected}
                  title="Upload image"
                >
                  {uploading ? "Uploading…" : "Upload"}
                </button>
                <button
                  type="button"
                  style={smallBtn}
                  onClick={(e) => {
                    e.stopPropagation();
                    setUploadErr("");
                    setSearchOpen(true);
                  }}
                  disabled={uploading || busy || !tenantServerUrl}
                  title="Find a photo of this dish online and save it"
                >
                  Search online
                </button>
                {displayPreviewSrc && (
                  <>
                    <button
                      type="button"
                      style={smallBtn}
                      onClick={(e) => {
                        e.stopPropagation();
                        setPhotoOpen(true);
                      }}
                      title="Expand photo"
                    >
                      Expand
                    </button>
                    <a
                      href={displayPreviewSrc}
                      target="_blank"
                      rel="noreferrer"
                      style={{
                        ...smallBtn,
                        textDecoration: "none",
                        display: "inline-block",
                      }}
                      title="Open image in new tab"
                    >
                      Open in new tab
                    </a>
                  </>
                )}
              </div>
              {!!uploadErr && (
                <div style={{ color: "#b3261e", fontSize: 12 }}>{uploadErr}</div>
              )}
            </div>
          </div>

          <div>
            <div style={{ fontSize: 12, color: "#555", marginBottom: 6 }}>
              Rating
            </div>
            <Stars
              value={Number(model.rating ?? 3)}
              onChange={(v) => setField("rating", v)}
            />
            <div
              style={{
                marginTop: 16,
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <input
                id="visible"
                type="checkbox"
                checked={!!model.visible}
                onChange={(e) => setField("visible", e.target.checked)}
              />
              <label htmlFor="visible">Show In App</label>
            </div>
          </div>
        </div>

        {/* Sides / Sizes / Specs */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap: 10,
            marginTop: 12,
          }}
        >
          {/* Sides */}
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <span style={{ fontSize: 12, color: "#555" }}>Order Sides</span>
            <div style={{ display: "flex", gap: 6 }}>
              {sidesOptions.length ? (
                <select
                  style={{ ...input, flex: 1 }}
                  value={String(sidesIndex)}
                  onChange={onChangeSides}
                >
                  {sidesOptions.map((o, i) => (
                    <option key={`side-${i}`} value={String(i)}>
                      {o.universal || o.id ? "★ " : ""}
                      {o.name} — {formatPriceDisplay(o.price)}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  style={{ ...input, background: "#f9f9f9", flex: 1 }}
                  value=""
                  placeholder="No sides configured"
                  readOnly
                />
              )}
              <button
                type="button"
                style={smallBtn}
                onClick={() => setEditSidesOpen(true)}
              >
                Manage
              </button>
            </div>
            <div style={{ fontSize: 11, color: "#888" }}>
              ★ = Universal side (shared across menu items).
            </div>
          </div>

          {/* Sizes */}
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <span style={{ fontSize: 12, color: "#555" }}>
              Order Sizes (portions)
            </span>
            <div style={{ display: "flex", gap: 6 }}>
              {sizesOptions.length ? (
                <select
                  style={{ ...input, flex: 1 }}
                  value={String(sizesIndex)}
                  onChange={onChangeSizes}
                >
                  {sizesOptions.map((o, i) => (
                    <option key={`size-${i}`} value={String(i)}>
                      {o.name} — {formatPriceDisplay(o.price)}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  style={{ ...input, background: "#f9f9f9", flex: 1 }}
                  value=""
                  placeholder="No sizes configured"
                  readOnly
                />
              )}
              <button
                type="button"
                style={smallBtn}
                onClick={() => setEditSizesOpen(true)}
              >
                Manage
              </button>
            </div>
            <div style={{ fontSize: 11, color: "#888" }}>
              Stored as <code>name&lt;&gt;price--</code>, selection = first item.
            </div>
          </div>

          {/* Specifications */}
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <span style={{ fontSize: 12, color: "#555" }}>
              Order Specifications
            </span>
            <div style={{ display: "flex", gap: 6 }}>
              {specOptions.length ? (
                <select
                  style={{ ...input, flex: 1 }}
                  value={String(specIndex)}
                  onChange={onChangeSpecs}
                >
                  {specOptions.map((o, i) => (
                    <option key={`spec-${i}`} value={String(i)}>
                      {o.name} — {formatPriceDisplay(o.price)}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  style={{ ...input, background: "#f9f9f9", flex: 1 }}
                  value=""
                  placeholder="No specifications configured"
                  readOnly
                />
              )}
              <button
                type="button"
                style={smallBtn}
                onClick={() => setEditSpecsOpen(true)}
              >
                Manage
              </button>
            </div>
            <div style={{ fontSize: 11, color: "#888" }}>
              Stored as <code>name&lt;&gt;price--</code>, selection = first item.
            </div>
          </div>
        </div>

        <div className="allergen-picker" style={{ marginTop: 14 }}>
          <div style={{ fontSize: 12, color: "#555", marginBottom: 6 }}>
            Allergens
            {selectedAllergenIds.length
              ? ` (${selectedAllergenIds.length} selected)`
              : ""}
          </div>
          <AllergenPicker
            catalog={allergenCatalog}
            selectedIds={selectedAllergenIds}
            onToggle={(key) => {
              const token = String(key || "").trim();
              if (!token) return;
              setSelectedAllergenIds((prev) => {
                const next = (prev || []).map(String);
                return next.includes(token)
                  ? next.filter((x) => x !== token)
                  : [...next, token];
              });
            }}
          />
        </div>

        {!!err && <div style={{ color: "#b3261e", marginTop: 10 }}>{err}</div>}

        {/* Footer buttons */}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 8,
            marginTop: 14,
          }}
        >
          {!isNew && (
            <button
              type="button"
              onClick={handleDelete}
              style={{
                padding: "8px 12px",
                borderRadius: 8,
                border: "1px solid #b3261e",
                background: "#fff",
                color: "#b3261e",
                cursor: "pointer",
              }}
            >
              Delete Item
            </button>
          )}
          <button
            type="button"
            style={btn}
            onClick={onClose}
            disabled={busy || uploading}
          >
            Cancel
          </button>
          <button
            type="button"
            style={btnPrimary}
            onClick={save}
            disabled={busy || uploading}
          >
            {busy ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </div>

      {/* Child modals */}
      {editSidesOpen && (
        <SmallModal
          title="Manage Sides"
          onClose={() => setEditSidesOpen(false)}
          width={780}
        >
          <SidesEditor
            options={sidesOptions}
            defaultIndex={sidesIndex}
            universalSides={universalSides}
            onSave={(list, defIdx, meta) => {
              applySidesList(list, defIdx, meta);
              setEditSidesOpen(false);
            }}
          />
        </SmallModal>
      )}
      {editSizesOpen && (
        <SmallModal title="Manage Sizes" onClose={() => setEditSizesOpen(false)}>
          <OptionEditor
            options={sizesOptions}
            defaultIndex={sizesIndex}
            onSave={(list, defIdx, meta) => {
              applySizesList(list, defIdx, meta);
              setEditSizesOpen(false);
            }}
          />
        </SmallModal>
      )}
      {editSpecsOpen && (
        <SmallModal
          title="Manage Specifications"
          onClose={() => setEditSpecsOpen(false)}
        >
          <OptionEditor
            options={specOptions}
            defaultIndex={specIndex}
            onSave={(list, defIdx, meta) => {
              applySpecsList(list, defIdx, meta);
              setEditSpecsOpen(false);
            }}
          />
        </SmallModal>
      )}
      {photoOpen && displayPreviewSrc && (
        <SmallModal
          title="Menu Item Photo"
          onClose={() => setPhotoOpen(false)}
          width={900}
        >
          <div style={{ display: "grid", gap: 10 }}>
            <div
              style={{
                width: "100%",
                maxHeight: "72vh",
                overflow: "auto",
                border: "1px solid #eee",
                borderRadius: 10,
                background: "#fff",
                display: "grid",
                placeItems: "center",
                padding: 8,
              }}
            >
              <img
                src={displayPreviewSrc}
                alt="menu item full"
                referrerPolicy="no-referrer"
                style={{ maxWidth: "100%", height: "auto", objectFit: "contain" }}
                onError={handlePreviewError}
              />
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <a href={displayPreviewSrc} target="_blank" rel="noreferrer" style={smallBtn}>
                Open in new tab
              </a>
              <button
                type="button"
                style={smallBtn}
                onClick={() => setPhotoOpen(false)}
              >
                Close
              </button>
            </div>
          </div>
        </SmallModal>
      )}
      {searchOpen && (
        <SmallModal
          title="Search for image online"
          onClose={() => setSearchOpen(false)}
          width={860}
        >
          <OnlineImageSearch
            itemName={String(model.name || "").trim()}
            itemDescription={String(model.description || "").trim()}
            existingFilename={(() => {
              const name = filenameFromValue(model.url);
              const lower = String(name).toLowerCase();
              if (
                !name ||
                ["none.jpg", "image.jpg", "placeholder.jpg", "placeholder.png"].includes(
                  lower
                )
              ) {
                return "";
              }
              return name;
            })()}
            tenantServerUrl={tenantServerUrl}
            onApplied={({ filename, remoteUrl, thumb }) => {
              const file = extractMenuImageFilename(filename) || filename;
              const savedPreview = buildMenuImagePreviewUrl(
                tenantServerUrl,
                file,
                Date.now()
              );
              setModel((m) => ({
                ...m,
                url: file,
                imagePreview: thumb || remoteUrl || savedPreview,
              }));
              setPreviewOverride("");
              setFileSelected(null);
              setPendingUpload(false);
              setUploadErr("");
              setSearchOpen(false);
            }}
          />
        </SmallModal>
      )}
    </div>
  );
}
